/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { BulkAssignAssignment, BulkAssignResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type BulkAssignConfigsMutationConfig = {
  onSuccess?: (data: BulkAssignResponse, variables: BulkAssignConfigsParams) => void;
  onError?: (error: Error, variables: BulkAssignConfigsParams) => void;
  onSettled?: (data: BulkAssignResponse | undefined, error: Error | null, variables: BulkAssignConfigsParams) => void;
  onMutate?: (variables: BulkAssignConfigsParams) => void;
};

export type BulkAssignConfigsParams = {
  assignments: BulkAssignAssignment[];
};

/**
 * Hook for bulk assigning configs to multiple nodes.
 *
 * Accepts a list of assignments where each specifies a nodeId, configId, and revisionNumber.
 * Server groups by (configId, revisionNumber) and assigns each group atomically.
 *
 * API Endpoint:
 * - POST /tenants/:tenantId/nodes/assign - Bulk assign configs to nodes
 *
 * @param config.onSuccess - Called after successful assignment
 * @param config.onError - Called if assignment fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 * @param config.onMutate - Called before mutation starts
 *
 * @returns React Query mutation result for bulk assigning configs
 */
export function useBulkAssignConfigsMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: BulkAssignConfigsMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutateAsync, isPending, isError, error } = useMutation<BulkAssignResponse, Error, BulkAssignConfigsParams>({
    mutationFn: async ({ assignments }: BulkAssignConfigsParams) => {
      return mgmtPlaneAPIClient.nodes.bulkAssignConfigs({
        tenantId: tenantId!,
        requestBody: {
          assignments,
        },
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutateAsync, isPending, isError, error };
}
